import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import api from '../services/api';
import Header from '../components/Header';
import Sidebar from '../components/Sidebar';
import Footer from '../components/Footer';
import authService from '../services/authService';
import './EscolherPrazo.css';

const formatarValor = (v) =>
  (Number(v) || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatarData = (d) => (d ? new Date(d).toLocaleDateString('pt-BR') : '-');

const labelFaixa = (dias) => (Number(dias) === 0 ? '24 a 48h úteis após solicitação' : `${dias} dias`);

function EscolherPrazo() {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enviandoCliente, setEnviandoCliente] = useState(null);
  const [grupos, setGrupos] = useState([]); // [{ cliente, faixas, ordens }]
  // Estado por cliente: { [clienteId]: { selecionadas:Set, dias:Number|null } }
  const [selecao, setSelecao] = useState({});

  const carregar = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await api.get('/ordens-servico/pendentes-escolha');
      const ordens = data.ordens || [];
      const faixasPorCliente = data.faixasPorCliente || {};

      // Agrupar OS por cliente
      const mapa = new Map();
      for (const os of ordens) {
        const cid = String(os.cliente?._id || os.cliente);
        if (!mapa.has(cid)) {
          mapa.set(cid, {
            cliente: os.cliente,
            faixas: faixasPorCliente[cid] || [],
            ordens: []
          });
        }
        mapa.get(cid).ordens.push(os);
      }
      setGrupos(Array.from(mapa.values()));
      // reset seleção
      const novaSelecao = {};
      for (const [cid] of mapa) novaSelecao[cid] = { selecionadas: new Set(), dias: null };
      setSelecao(novaSelecao);
    } catch (error) {
      console.error(error);
      toast.error('Erro ao carregar ordens pendentes');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    setUser(currentUser);
    if (currentUser?.role !== 'fornecedor') {
      toast.error('Acesso restrito a fornecedores');
      navigate('/dashboard');
      return;
    }
    carregar();
  }, [navigate, carregar]);

  const toggleOS = (cid, osId) => {
    setSelecao(prev => {
      const atual = prev[cid] || { selecionadas: new Set(), dias: null };
      const nova = new Set(atual.selecionadas);
      if (nova.has(osId)) nova.delete(osId);
      else nova.add(osId);
      return { ...prev, [cid]: { ...atual, selecionadas: nova } };
    });
  };

  const toggleTodas = (cid, ordens) => {
    setSelecao(prev => {
      const atual = prev[cid] || { selecionadas: new Set(), dias: null };
      const todasMarcadas = ordens.every(o => atual.selecionadas.has(String(o._id)));
      const nova = todasMarcadas ? new Set() : new Set(ordens.map(o => String(o._id)));
      return { ...prev, [cid]: { ...atual, selecionadas: nova } };
    });
  };

  const setDias = (cid, dias) => {
    setSelecao(prev => ({
      ...prev,
      [cid]: { ...(prev[cid] || { selecionadas: new Set() }), dias: dias === '' ? null : Number(dias) }
    }));
  };

  // Cálculo (estimativa, antes de impostos/retenções)
  const calcular = (grupo, cid) => {
    const est = selecao[cid] || { selecionadas: new Set(), dias: null };
    const selecionadas = grupo.ordens.filter(o => est.selecionadas.has(String(o._id)));
    const bruto = selecionadas.reduce((s, o) => s + (Number(o.valorFinal) || 0), 0);
    const faixa = grupo.faixas.find(f => Number(f.dias) === Number(est.dias));
    const taxa = faixa ? Number(faixa.taxa) : null;
    const valorTaxa = taxa != null ? bruto * (taxa / 100) : 0;
    const liquido = bruto - valorTaxa;
    return { qtd: selecionadas.length, bruto, taxa, valorTaxa, liquido, dias: est.dias };
  };

  const confirmar = async (grupo, cid) => {
    const est = selecao[cid];
    if (!est || est.selecionadas.size === 0) {
      toast.warn('Selecione ao menos uma OS');
      return;
    }
    if (est.dias == null) {
      toast.warn('Escolha o prazo de recebimento');
      return;
    }
    const { qtd, liquido, taxa } = calcular(grupo, cid);
    const ok = window.confirm(
      `Confirmar ${labelFaixa(est.dias)} (taxa ${taxa}%) para ${qtd} OS?\n` +
      `Líquido estimado: ${formatarValor(liquido)}\n\n` +
      `Será gerada a ordem de pagamento automaticamente. Esta ação é definitiva.`
    );
    if (!ok) return;

    try {
      setEnviandoCliente(cid);
      const { data } = await api.post('/ordens-servico/escolher-pagamento', {
        ordensServicoIds: Array.from(est.selecionadas),
        tipoPagamento: `faixa:${est.dias}`
      });
      const r = (data.resultados || [])[0];
      toast.success(
        r
          ? `OP ${r.codigoOP || ''} gerada — líquido ${formatarValor(r.valor)} (${qtd} OS)`
          : 'Pagamento gerado com sucesso'
      );
      await carregar();
    } catch (error) {
      console.error(error);
      toast.error(error.response?.data?.message || 'Erro ao gerar pagamento');
    } finally {
      setEnviandoCliente(null);
    }
  };

  const totalOS = grupos.reduce((s, g) => s + g.ordens.length, 0);

  return (
    <div className="app-container">
      <Header user={user} />
      <div className="main-wrapper">
        <Sidebar user={user} />
        <main className="main-content">
          <div className="page-header">
            <div>
              <h1>⚡ Escolher Prazo de Recebimento</h1>
              <p className="page-subtitle">
                Selecione as OS autorizadas e escolha o prazo. A taxa varia conforme o prazo e a
                ordem de pagamento é gerada automaticamente.
              </p>
            </div>
            <span className="badge-total">{totalOS} OS pendente(s)</span>
          </div>

          {loading ? (
            <div className="loading-state"><p>Carregando...</p></div>
          ) : grupos.length === 0 ? (
            <div className="empty-state">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                <path d="M9 11l3 3L22 4" />
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11" />
              </svg>
              <p>Nenhuma OS aguardando escolha de prazo</p>
              <span>Quando houver OS autorizadas destes clientes, elas aparecerão aqui.</span>
            </div>
          ) : (
            grupos.map((grupo) => {
              const cid = String(grupo.cliente?._id || grupo.cliente);
              const est = selecao[cid] || { selecionadas: new Set(), dias: null };
              const calc = calcular(grupo, cid);
              const todasMarcadas = grupo.ordens.every(o => est.selecionadas.has(String(o._id)));
              return (
                <div className="cliente-card" key={cid}>
                  <div className="cliente-card-header">
                    <h2>{grupo.cliente?.razaoSocial || grupo.cliente?.nomeFantasia || 'Cliente'}</h2>
                    <span className="cliente-os-count">{grupo.ordens.length} OS</span>
                  </div>

                  {grupo.faixas.length === 0 && (
                    <div className="alerta-inline">
                      ⚠️ Este cliente ainda não tem faixas de prazo/taxa configuradas. Fale com o administrador.
                    </div>
                  )}

                  <div className="table-responsive">
                    <table className="data-table">
                      <thead>
                        <tr>
                          <th>
                            <input
                              type="checkbox"
                              checked={todasMarcadas}
                              onChange={() => toggleTodas(cid, grupo.ordens)}
                              aria-label="Selecionar todas"
                            />
                          </th>
                          <th>OS</th>
                          <th>Serviço</th>
                          <th>Veículo/Placa</th>
                          <th>Referência</th>
                          <th>Valor</th>
                        </tr>
                      </thead>
                      <tbody>
                        {grupo.ordens.map((os) => {
                          const osId = String(os._id);
                          return (
                            <tr key={osId} className={est.selecionadas.has(osId) ? 'row-selected' : ''}>
                              <td>
                                <input
                                  type="checkbox"
                                  checked={est.selecionadas.has(osId)}
                                  onChange={() => toggleOS(cid, osId)}
                                />
                              </td>
                              <td><strong>{os.codigo || os.numeroOrdemServico}</strong></td>
                              <td>{os.tipoServicoSolicitado?.nome || '-'}</td>
                              <td>{os.veiculo || os.placa || '-'}</td>
                              <td>{formatarData(os.dataReferencia)}</td>
                              <td><strong>{formatarValor(os.valorFinal)}</strong></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  <div className="escolha-barra">
                    <div className="escolha-prazo">
                      <label>Prazo de recebimento:</label>
                      <select
                        value={est.dias == null ? '' : String(est.dias)}
                        onChange={(e) => setDias(cid, e.target.value)}
                        disabled={grupo.faixas.length === 0}
                      >
                        <option value="">Selecione...</option>
                        {grupo.faixas.map((f) => (
                          <option key={f.dias} value={String(f.dias)}>
                            {labelFaixa(f.dias)} — taxa {f.taxa}%
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="escolha-resumo">
                      <div><span>OS selecionadas</span><strong>{calc.qtd}</strong></div>
                      <div><span>Bruto</span><strong>{formatarValor(calc.bruto)}</strong></div>
                      <div>
                        <span>Taxa {calc.taxa != null ? `(${calc.taxa}%)` : ''}</span>
                        <strong className="valor-taxa">- {formatarValor(calc.valorTaxa)}</strong>
                      </div>
                      <div className="destaque">
                        <span>Líquido estimado</span>
                        <strong>{formatarValor(calc.liquido)}</strong>
                      </div>
                    </div>

                    <button
                      className="btn-confirmar"
                      onClick={() => confirmar(grupo, cid)}
                      disabled={enviandoCliente === cid || calc.qtd === 0 || est.dias == null}
                    >
                      {enviandoCliente === cid ? 'Gerando...' : 'Gerar pagamento'}
                    </button>
                  </div>
                  <p className="nota-estimativa">
                    * Líquido estimado antes de impostos/retenções. O valor final da OP considera as retenções do cliente.
                  </p>
                </div>
              );
            })
          )}
        </main>
      </div>
      <Footer />
    </div>
  );
}

export default EscolherPrazo;
